import { ID, ImageGravity, Query } from "appwrite";

import { appwriteConfig, account, databases, storage, functions } from "./config";
import { IUpdatePost, INewPost, IUpdateUser, DealDocument, DealAdjustmentDocument } from "@/types";
import { backendRequest, signInBackendOAuth } from "@/lib/backend/api";

// ============================================================
// AUTH
// ============================================================

// ============================== SIGN UP

// ============================== SAVE USER TO DB


const withAppwriteId = <T extends Record<string, any>>(item: T): T => {
  if (!item || typeof item !== "object") {
    return item;
  }

  const normalizedId = item.$id ?? item.id ?? item._id;
  return {
    ...item,
    $id: normalizedId,
    id: item.id ?? normalizedId,
  };
};

export async function saveUserToDB(user: {
  accountId: string;
  emailOrPhone: string;
  name: string;
  imageUrl: string;
  username?: string;
  acceptedSLA?: boolean;
}) {
  try {
    // First check if user already exists
    const existingUser = await getExistingUser(user);
    if (existingUser.documents.length > 0) {
      return existingUser.documents[0]; // Return the existing user document
    }

    // Only create new document if user doesn't exist
    const newUser = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.userCollectionId,
      ID.unique(),
      user
    );
     await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.trustScoreCollectionId,
      ID.unique(),
      {
        userId: newUser.$id,
        score: 50,
        level: 'bronze',
        type: 'buyer', // Default to buyer, can be updated later
        updatedAt: new Date().toISOString()
      }
    );
    return newUser;
  } catch (error) {
    console.error("Error in saveUserToDB:", error);
    throw error; // Throw error to handle it in the calling function
  }
}

// ============================== SIGN IN
export async function signInAccount(user: { emailOrPhone: string; password: string }) {
  try {
    const session = await account.createEmailPasswordSession(user.emailOrPhone, user.password);

    return session;
  } catch (error) {
    console.log(error);
  }
}

// ============================== GET ACCOUNT
export async function getAccount() {
  try {
    const currentAccount = await account.get();
    return currentAccount;
  } catch (error) {
    console.log(error);
  }
}

// ============================== GET USER
export async function getCurrentUser() {
  try {
    return await backendRequest<any>("/api/v1/users/me");
  } catch (error) {
    console.log(error);
    return null;
  }
}

export async function getExistingUser(currentAccount: any) {
  const existingUser = await databases.listDocuments(
    appwriteConfig.databaseId,
    appwriteConfig.userCollectionId,
    [Query.equal("accountId", currentAccount.accountId)]
  );
  return existingUser;
}

export async function getExistingUserByPhoneNumber(phone: string) {
        const response = await functions.createExecution(
          appwriteConfig.checkExistingUser,
          JSON.stringify({
            phone: phone,
          })
        );
        return JSON.parse(response.responseBody);
}

export async function getExistingUserForGoogle(currentAccount: any) {
  const existingUser = await databases.listDocuments(
    appwriteConfig.databaseId,
    appwriteConfig.userCollectionId,
    [Query.equal("accountId", currentAccount.$id)]
  );
  return existingUser;
}

// ============================== SIGN OUT
export async function signOutAccount() {
  try {
    const session = await account.deleteSession("current");

    return session;
  } catch (error) {
    console.log(error);
  }
}

// ============================================================
// POSTS
// ============================================================

// ============================== CREATE POST
export async function createPost(post: INewPost) {
  try {
    if (!post.userId) {
      throw new Error("User ID is required to create a post");
    }

    // Handle multiple files upload
    let uploadedFiles: any[] = [];
    let fileUrls: string[] = [];
    let media: Array<{ fileId: string; url: string; mediaType: string }> = [];

    if (post.file && post.file.length > 0) {
      // Upload all files
      for (const file of post.file) {
        try {
          const uploadedFile = await uploadFile(file);
          if (!uploadedFile) {
            throw new Error(`Failed to upload file: ${file.name}`);
          }

          const fileType = file.type;
          const fileUrl = fileType.startsWith("image")
            ? getFilePreview(uploadedFile.$id)
            : getFileView(uploadedFile.$id);

          if (!fileUrl) {
            await deleteFile(uploadedFile.$id);
            throw new Error(`Failed to get URL for file: ${file.name}`);
          }

          uploadedFiles.push(uploadedFile);
          fileUrls.push(fileUrl);
          media.push({
            fileId: uploadedFile.$id,
            url: fileUrl,
            mediaType: file.type || "application/octet-stream",
          });
        } catch (fileError) {
          // Clean up already uploaded files on error
          for (const uploaded of uploadedFiles) {
            try {
              await deleteFile(uploaded.$id);
            } catch (deleteError) {
              console.error(`Error deleting file ${uploaded.$id}:`, deleteError);
            }
          }
          throw fileError;
        }
      }
    }

    // Convert tags into array
    const tags = post.tags?.replace(/ /g, "").split(",") || [];

    // Create post - WITH isCommercialPost field
    const newPost = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.postCollectionId,
      ID.unique(),
      {
        creatorId: post.userId,
        individual: post.individual,
        category: post.dealType,
        caption: post.caption,
        imageUrl: fileUrls.length > 0 ? fileUrls : null,
        imageId: uploadedFiles.length > 0 ? uploadedFiles.map(f => f.$id) : null,
        media,
        location: post.location || null,
        tags: tags,
        isTrainingAvailable: post.isTrainingAvailable || false,
        isSellingPost: post.isSellingPost || false,
        isCommercialPost: post.isCommercialPost ?? true // ← NEW: Default true for create post form
      }
    );

    if (!newPost) {
      // Clean up uploaded files if post creation fails
      for (const uploadedFile of uploadedFiles) {
        try {
          await deleteFile(uploadedFile.$id);
        } catch (deleteError) {
          console.error(`Error deleting file ${uploadedFile.$id}:`, deleteError);
        }
      }
      throw new Error("Failed to create post document");
    }

    return newPost;
  } catch (error) {
    console.log(error);
    throw error;
  }
}
// ============================== UPLOAD FILE
export async function uploadFile(file: File) {
  try {
    const uploadedFile = await storage.createFile(
      appwriteConfig.storageId,
      ID.unique(),
      file
    );

    return uploadedFile;
  } catch (error) {
    console.log(error);
  }
}

// ============================== GET FILE URL
export function getFilePreview(fileId: string): string {
  try {
    const fileUrl = storage.getFilePreview(
      appwriteConfig.storageId,
      fileId,
      2000,
      2000,
      ImageGravity.Top,
      100
    );

    if (!fileUrl) throw Error;

    // Convert URL object to string
    return fileUrl.toString();
  } catch (error) {
    console.log(error);
    return ""; // Return empty string or you could return null/undefined
  }
}

// Add a proper getFileView function for videos
export function getFileView(fileId: string): string {
  try {
    const fileUrl = storage.getFileView(
      appwriteConfig.storageId,
      fileId
    );

    if (!fileUrl) throw Error;

    return fileUrl.toString();
  } catch (error) {
    console.log(error);
    return "";
  }
}

// ============================== DELETE FILE
export async function deleteFile(fileId: string) {
  try {
    await storage.deleteFile(appwriteConfig.storageId, fileId);

    return { status: "ok" };
  } catch (error) {
    console.log(error);
  }
}

// ============================== GET POSTS
export async function searchPosts(searchTerm: string) {
  try {
    const normalizedSearchTerm = searchTerm.trim();
    if (!normalizedSearchTerm) {
      return { documents: [], total: 0 };
    }

    const response = await backendRequest<{ documents: any[]; total: number }>(
      `/api/v1/posts?limit=50&offset=0&query=${encodeURIComponent(normalizedSearchTerm)}`
    );

    const documents = Array.isArray(response?.documents) ? response.documents : [];
    return {
      documents: documents.map(withAppwriteId),
      total: response?.total ?? documents.length,
    };
  } catch (error) {
    console.error('Search Posts Error:', error);
    return { 
      documents: [], 
      total: 0,
      error: error instanceof Error ? error.message : 'An unknown error occurred'
    };
  }
}

export async function getInfinitePosts({ pageParam }: { pageParam: number }) {
  const queries: any[] = [Query.orderDesc("$updatedAt"), Query.limit(9)];

  if (pageParam) {
    queries.push(Query.cursorAfter(pageParam.toString()));
  }

  try {
    const posts = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.postCollectionId,
      queries
    );

    if (!posts) throw Error;

    return posts;
  } catch (error) {
    console.log(error);
  }
}

// ============================== GET POST BY ID
export async function getPostById(postId?: string) {
  if (!postId) throw Error;

  try {
    const post = await databases.getDocument(
      appwriteConfig.databaseId,
      appwriteConfig.postCollectionId,
      postId
    );

    if (!post) throw Error;

    return post;
  } catch (error) {
    console.log(error);
  }
}

// ============================== UPDATE POST
export async function updatePost(post: IUpdatePost) {
  try {
    // ✅ IMPROVED: Better validation
    if (!post.postId) {
      throw new Error("Post ID is required for update");
    }

    const hasFileToUpdate = post.file && post.file.length > 0;
    const newlyUploadedFileIds: string[] = [];

    let media = post.media ?? (() => {
      const urls = Array.isArray(post.imageUrl) ? post.imageUrl : post.imageUrl ? [post.imageUrl] : [];
      const ids = Array.isArray(post.imageId) ? post.imageId : post.imageId ? [post.imageId] : [];
      return urls.map((url, index) => ({ fileId: ids[index] ?? url, url, mediaType: "application/octet-stream" }));
    })();

    // ✅ IMPROVED: Handle new file uploads
    if (hasFileToUpdate && post.file) {
      let uploadedFiles: any[] = [];
      const uploadedMedia: Array<{ fileId: string; url: string; mediaType: string }> = [];

      // Upload all new files
      for (const file of post.file) {
        try {
          const uploadedFile = await uploadFile(file);
          if (!uploadedFile) {
            throw new Error(`Failed to upload file: ${file.name}`);
          }

          const fileType = file.type;
          const fileUrl = fileType.startsWith("image")
            ? getFilePreview(uploadedFile.$id)
            : getFileView(uploadedFile.$id);
          
          if (!fileUrl) {
            // Clean up the current file
            await deleteFile(uploadedFile.$id);
            throw new Error(`Failed to get URL for file: ${file.name}`);
          }

          uploadedFiles.push(uploadedFile);
          newlyUploadedFileIds.push(uploadedFile.$id);
          uploadedMedia.push({ fileId: uploadedFile.$id, url: fileUrl, mediaType: file.type || "application/octet-stream" });
        } catch (fileError) {
          // Clean up already uploaded files on error
          console.error(`Error uploading file ${file.name}:`, fileError);
          for (const uploaded of uploadedFiles) {
            try {
              await deleteFile(uploaded.$id);
            } catch (deleteError) {
              console.error(`Error deleting file ${uploaded.$id}:`, deleteError);
            }
          }
          throw fileError;
        }
      }

      media = [...media, ...uploadedMedia];
    }

    // ✅ IMPROVED: Better tag processing
    const tags = Array.isArray(post.tags)
      ? post.tags.map(tag => tag.trim()).filter(tag => tag.length > 0)
      : typeof post.tags === "string"
      ? post.tags
          .split(",")
          .map(tag => tag.trim())
          .filter(tag => tag.length > 0)
      : [];

    // ✅ IMPROVED: Create update object with only defined values
    const updateData: any = {
      individual: post.individual || "",
      caption: post.caption || "",
      category: post.dealType,
      location: post.location || "",
      tags: tags,
      isTrainingAvailable: post.isTrainingAvailable || false,
      isSellingPost: post.isSellingPost || false,
      isCommercialPost: post.isCommercialPost ?? true, // ← NEW: Default true for update
      $updatedAt: new Date().toISOString(),
      isActivated: false,
      media,
    };

    // ✅ IMPROVED: Only update isActivated if explicitly provided
    if (typeof post.isActivated === 'boolean') {
      updateData.isActivated = post.isActivated;
    }

    // Update post
    const updatedPost = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.postCollectionId,
      post.postId,
      updateData
    );

    if (!updatedPost) {
      // Delete new files that have been recently uploaded
      if (hasFileToUpdate) {
        for (const imageId of newlyUploadedFileIds) {
          try {
            await deleteFile(imageId);
          } catch (deleteError) {
            console.error(`Error deleting newly uploaded file ${imageId}:`, deleteError);
          }
        }
      }
      throw new Error("Failed to update post document");
    }

    // ✅ IMPROVED: Safely delete old files after successful update
    if (post.imageId) {
      const retainedIds = new Set(media.map(item => item.fileId));
      const oldImageIds = (Array.isArray(post.imageId) ? post.imageId : [post.imageId]).filter(imageId => !retainedIds.has(imageId));
      
      // Use Promise.allSettled to handle partial failures
      const deletePromises = oldImageIds.map(async (imageId) => {
        try {
          return await deleteFile(imageId);
        } catch (error) {
          console.error(`Error deleting old file ${imageId}:`, error);
          return null;
        }
      });

      await Promise.allSettled(deletePromises);
    }

    return updatedPost;
  } catch (error) {
    console.error("Error in updatePost:", error);
    throw error; // ✅ IMPROVED: Re-throw for proper error handling
  }
}

// ============================== DELETE POST
export async function deletePost(postId?: string, imageId?: string) {
  if (!postId || !imageId) return;

  try {
    const statusCode = await databases.deleteDocument(
      appwriteConfig.databaseId,
      appwriteConfig.postCollectionId,
      postId
    );

    if (!statusCode) throw Error;

    await deleteFile(imageId);

    return { status: "Ok" };
  } catch (error) {
    console.log(error);
  }
}

// ============================== LIKE / UNLIKE POST
export async function likePost(postId: string, likesArray: string[]) {
  try {
    const updatedPost = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.postCollectionId,
      postId,
      {
        likedBy: likesArray, // ✅ Updated field name
      }
    );

    if (!updatedPost) throw Error;

    return updatedPost;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function savePost(postId: string, savedByArray: string[]) {
  try {
    const updatedPost = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.postCollectionId,
      postId,
      {
        savedBy: savedByArray, // ✅ Update the savedBy array directly
      }
    );

    if (!updatedPost) throw Error;

    return updatedPost;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

// ============================== DELETE SAVED POST
export async function deleteSavedPost(savedRecordId: string) {
  try {
    const statusCode = await databases.deleteDocument(
      appwriteConfig.databaseId,
      appwriteConfig.savesCollectionId,
      savedRecordId
    );

    if (!statusCode) throw Error;

    return { status: "Ok" };
  } catch (error) {
    console.log(error);
  }
}

// ============================== GET USER'S POST
export async function getUserPosts(userId?: string) {
  if (!userId) return;

  try {
    const posts = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.postCollectionId,
      [
        Query.equal("creatorId", userId), 
        Query.equal("isActivated", true), // Only show activated posts for public
        Query.orderDesc("$createdAt")
      ]
    );

    if (!posts) throw Error;

    return posts;
  } catch (error) {
    console.log(error);
  }
}

// ============================== GET POPULAR POSTS (BY HIGHEST LIKE COUNT)
export async function getRecentPosts({
  pageParam = 0,
  filterBy = {},
}: {
  pageParam?: number;
  filterBy?: Record<string, string | boolean>;
}) {
  try {
    const queries = [
      Query.orderDesc("$createdAt"),
      Query.limit(5),
      Query.offset(pageParam * 5),
    ];

    // Apply dynamic filters
    Object.entries(filterBy).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (key === "category" && value !== "all") {
          queries.push(Query.equal(key, value));
        } else if (typeof value === "boolean") {
          queries.push(Query.equal(key, value));
        } else if (key !== "category") {
          queries.push(Query.equal(key, value));
        }
      }
    });

    // If no isActivated filter is provided, default to true (for public access)
    if (!filterBy.hasOwnProperty('isActivated')) {
      queries.push(Query.equal("isActivated", true));
    }

    const posts = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.postCollectionId,
      queries
    );

    if (!posts) throw new Error("No posts found");

    return posts;
  } catch (error) {
    console.error("Error fetching posts:", error);
    throw error;
  }
}

export async function getPublicPosts({
  pageParam = 0,
  category = "all",
}: {
  pageParam?: number;
  category?: string;
}) {
  try {
    const queries = [
      Query.orderDesc("$createdAt"),
      Query.limit(5),
      Query.offset(pageParam * 5),
      Query.equal("isActivated", true), // Only activated posts
    ];

    // Add category filter if not "all"
    if (category && category !== "all") {
      queries.push(Query.equal("category", category));
    }

    const posts = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.postCollectionId,
      queries
    );

    if (!posts) throw new Error("No posts found");

    return posts;
  } catch (error) {
    console.error("Error fetching public posts:", error);
    throw error;
  }
}

// ============================================================
// USER
// ============================================================

// ============================== GET USERS
export async function getUsers(limit?: number) {
  const queries: any[] = [Query.orderDesc("$createdAt")];

  if (limit) {
    queries.push(Query.limit(limit));
  }

  try {
    const users = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.userCollectionId,
      queries
    );

    if (!users) throw Error;

    return users;
  } catch (error) {
    console.log(error);
  }
}

// ============================== GET USER BY ID
export async function getUserById(userId: string) {
  try {
    const user = await databases.getDocument(
      appwriteConfig.databaseId,
      appwriteConfig.userCollectionId,
      userId
    );

    if (!user) throw Error;

    return user || null;
  } catch (error) {
    console.error("Error fetching user:", error);
    return null;

  }
}

const normalizeFollowRecord = (follow: any, fallbackFollowerId?: string, fallbackFollowingId?: string) => {
  if (!follow) {
    return null;
  }

  const followId = follow.id ?? follow.$id ?? follow.Id ?? follow.followId ?? follow.FollowId;
  return {
    ...follow,
    $id: followId ?? follow.id ?? follow.$id ?? null,
    id: follow.id ?? follow.Id ?? followId ?? null,
    followerId: follow.followerId ?? follow.FollowerId ?? fallbackFollowerId ?? null,
    followingId: follow.followingId ?? follow.FollowingId ?? fallbackFollowingId ?? null,
    createdAtUtc: follow.createdAtUtc ?? follow.CreatedAtUtc ?? follow.createdAt ?? follow.CreatedAt ?? null,
  };
};

// ============================== UPDATE USER
export async function updateUser(user: IUpdateUser) {
  const hasFileToUpdate = user.file.length > 0;
  try {
    let image = {
      imageUrl: user.imageUrl,
      imageId: user.imageId,
    };

    if (hasFileToUpdate) {
      // Upload new file to appwrite storage
      const uploadedFile = await uploadFile(user.file[0]);
      if (!uploadedFile) throw Error;

      // Get new file url
      const fileUrl = getFilePreview(uploadedFile.$id);
      if (!fileUrl) {
        await deleteFile(uploadedFile.$id);
        throw Error;
      }

      image = { ...image, imageUrl: fileUrl, imageId: uploadedFile.$id };
    }

    //  Update user
    const updatedUser = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.userCollectionId,
      user.userId,
      {
        name: user.name,
        bio: user.bio,
        imageUrl: image.imageUrl,
        imageId: image.imageId,
        platformFee: user.platformFee,
        isKYCDone: user.isKYCDone
      }
    );

    // Failed to update
    if (!updatedUser) {
      // Delete new file that has been recently uploaded
      if (hasFileToUpdate) {
        await deleteFile(image.imageId ?? '');
      }
      // If no new file uploaded, just throw error
      throw Error;
    }

    // Safely delete old file after successful update
    if (user.imageId && hasFileToUpdate) {
      await deleteFile(user.imageId);
    }

    return updatedUser;
  } catch (error) {
    console.log(error);
  }
}

//=============================== Sending and Receiving messages
export async function sendMessage(messageDetails: { 
  senderId: string; 
  recipientId: string; 
  message: string; 
  attachments?: File[]; 
  dealId?: string;
  postId?: string;
  isAgentMessage?: boolean;
}) {
  try {
    const messageData: any = {
      sender_id: messageDetails.senderId,
      recipient_id: messageDetails.recipientId,
      message: messageDetails.message,
      timestamp: new Date().toISOString(),
      is_read: false,
      hasAttachments: false,
      dealId: messageDetails.dealId,
      postId: messageDetails.postId,
      is_agent_message: messageDetails.isAgentMessage ?? false,
    };

    // Handle attachments if they exist
    if (messageDetails.attachments && messageDetails.attachments.length > 0) {
      try {
        // Upload each file to storage
        const uploadPromises = messageDetails.attachments.map(file => {
          // The createFile method expects parameters in a different format
          // Check the Appwrite SDK version you're using - this is for newer versions
          return storage.createFile(
            appwriteConfig.storageId,
            ID.unique(), // Using Appwrite's ID.unique() instead of string 'unique()'
            file
            // Note: permissions and other options would be passed differently depending on SDK version
          );
        });
        
        const uploadResults = await Promise.all(uploadPromises);
        const attachmentIds = uploadResults.map(result => result.$id);
        
        // Store attachment IDs and metadata
        messageData.attachment_ids = attachmentIds;
        messageData.hasAttachments = true;
        
        // Create metadata with file information
        const attachmentMetadata = messageDetails.attachments.map(file => ({
          name: file.name,
          type: file.type,
          size: file.size
        }));
        
        messageData.attachment_metadata_json = JSON.stringify(attachmentMetadata);
      } catch (uploadError) {
        console.error("Failed to upload attachments:", uploadError);
        messageData.hasAttachments = false;
        delete messageData.attachment_ids;
        delete messageData.attachment_metadata_json;
      }
    }

    return await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.messagesCollectionId,
      ID.unique(),
      messageData
    );
  } catch (error) {
    console.error("Failed to send message:", error);
    throw error;
  }
}

export async function fetchMessages(senderId: string, recipientId: string, _currentUserId: string, dealId?: string) {
  try {
    const queries = [
      Query.equal('sender_id', senderId),
      Query.equal('recipient_id', recipientId),
      Query.orderAsc('$createdAt'),
      Query.limit(100),
    ];
    if (dealId) queries.push(Query.equal('dealId', dealId));

    // The backend conversation endpoint already returns both directions.
    const response = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.messagesCollectionId,
      queries
    );
    return response.documents;

  } catch (error) {
    console.error('Error fetching chat messages:', error);
    throw error; // Re-throw to let React Query handle the error
  }
}

export async function fetchMessagesForUser(userId: string, isAgent = false) {
  try {
    if (isAgent) {
      // Agent sees ALL messages in the system
      const allMessages = await databases.listDocuments(
        appwriteConfig.databaseId,
        appwriteConfig.messagesCollectionId,
        [
          Query.orderDesc('$createdAt'),
          Query.limit(1000) // Increase limit for agent to see more conversations
        ]
      );
      return allMessages;
    } else {
      // Regular user - optimized with OR query
      const userMessages = await databases.listDocuments(
        appwriteConfig.databaseId,
        appwriteConfig.messagesCollectionId,
        [
          Query.or([
            Query.equal('sender_id', userId),
            Query.equal('recipient_id', userId)
          ]),
          Query.orderDesc('$createdAt'),
          Query.limit(500)
        ]
      );
      return userMessages;
    }
  } catch (error) {
    console.error('Error fetching messages for user:', error);
    return { documents: [] };
  }
}

export async function fetchUsersByIds(userIds: string[]) {
  if (!userIds.length) return [];
  
  try {
    // Appwrite has a limit on Query.equal with arrays, so we batch the requests
    const batchSize = 25; // Adjust based on Appwrite's Query limits
    const batches: string[][] = [];
    
    for (let i = 0; i < userIds.length; i += batchSize) {
      const batch = userIds.slice(i, i + batchSize);
      batches.push(batch);
    }
    
    const userPromises = batches.map(batch => 
      databases.listDocuments(
        appwriteConfig.databaseId,
        appwriteConfig.userCollectionId,
        [Query.equal('$id', batch)]
      )
    );
    
    const results = await Promise.all(userPromises);
    return results.flatMap(result => result.documents);
  } catch (error) {
    console.error('Error fetching users by IDs:', error);
    return [];
  }
}

export const getLastMessages = async (userId: string) => {
  try {
    // Get messages where user is sender
    const sentMessages = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.messagesCollectionId,
      [
        Query.equal('sender_id', userId),
        Query.orderDesc('timestamp'),
        Query.limit(100)  // Adjust limit as needed
      ]
    );

    // Get messages where user is recipient
    const receivedMessages = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.messagesCollectionId,
      [
        Query.equal('recipient_id', userId),
        Query.orderDesc('timestamp'),
        Query.limit(100)  // Adjust limit as needed
      ]
    );

    // Combine all messages
    const allMessages = [...sentMessages.documents, ...receivedMessages.documents];

    // Create a map to store the last message for each conversation
    const conversationMap = new Map();

    allMessages.forEach(message => {
      const otherUserId = message.sender_id === userId 
        ? message.recipient_id 
        : message.sender_id;
      
      if (!conversationMap.has(otherUserId) || 
          new Date(message.timestamp).getTime() > 
          new Date(conversationMap.get(otherUserId).timestamp).getTime()) {
        conversationMap.set(otherUserId, {
          ...message,
          otherUserId,
          isFromUser: message.sender_id === userId
        });
      }
    });
  // Convert map to array and sort by timestamp
  const lastMessages = Array.from(conversationMap.values())
  .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

return lastMessages;

} catch (error) {
console.error('Error fetching last messages:', error);
throw error;
}
};

export const initiateGoogleAuth = async (_account: any, tokenOrOptions?: string | { token?: string; idToken?: string; credential?: string; deviceName?: string; email?: string; displayName?: string }) => {
  const token = typeof tokenOrOptions === "string"
    ? tokenOrOptions
    : tokenOrOptions?.token ?? tokenOrOptions?.idToken ?? tokenOrOptions?.credential;

  if (!token) {
    console.warn("Google OAuth now uses the backend token exchange path. Provide an id token or credential.");
    return { success: false, provider: "google", message: "Google OAuth requires an id token." };
  }

  return await signInBackendOAuth(
    "google",
    token,
    typeof tokenOrOptions === "object" ? tokenOrOptions?.deviceName : undefined,
    typeof tokenOrOptions === "object" ? tokenOrOptions?.email : undefined,
    typeof tokenOrOptions === "object" ? tokenOrOptions?.displayName : undefined
  );
};

export const deleteAllSessions = async () => {
  try {
    // Clear local storage first to prevent cookie issues
    localStorage.removeItem("cookieFallback");
    
    try {
      const sessions = await account.listSessions();
      await Promise.all(
        sessions.sessions.map(session => account.deleteSession(session.$id))
      );
    } catch (error) {
      // If there are no sessions to delete, that's fine
      console.log("No active sessions to delete");
    }
    
    return true;
  } catch (error) {
    console.error("Error in deleteAllSessions:", error);
    // Don't throw the error as it's not critical
    return false;
  }
};

// ✅ Create Escrow Transaction
export async function createEscrowTransaction(
  postId: string,
  buyerId: string,
  amount: number,
  commissionFee?: number // Optional
) {
  try {
    const post = await getPostById(postId);
    if (!post) throw new Error("Post not found");

    const escrow = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.escrowCollectionId,
      ID.unique(),
      {
        postId,
        buyerId,
        sellerId: post.sellerId,
        amount,
        commissionFee: commissionFee || null, // Optional commission
        status: "pending",
        paymentStatus: "held",
        sellerPayoutId: null, // Will be set when releasing payment
      }
    );

    return escrow;
  } catch (error) {
    console.error("Error in createEscrowTransaction:", error);
    throw new Error("Failed to create escrow transaction");
  }
}

// ✅ Release Payment (Used when the transaction is successfully completed)
export async function releasePayment(escrowId: string, sellerPayoutId: string) {
  try {
    const escrow = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.escrowCollectionId,
      escrowId,
      {
        status: "completed",
        paymentStatus: "released",
        sellerPayoutId, // Required when payment is released
      }
    );

    return escrow;
  } catch (error) {
    console.error("Error in releasePayment:", error);
    throw new Error("Failed to release payment");
  }
}

// ✅ Refund Payment (When a dispute is resolved in favor of the buyer)
export async function refundPayment(escrowId: string) {
  try {
    const escrow = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.escrowCollectionId,
      escrowId,
      {
        status: "disputed",
        paymentStatus: "refunded",
      }
    );

    return escrow;
  } catch (error) {
    console.error("Error in refundPayment:", error);
    throw new Error("Failed to refund payment");
  }
}

// ✅ Auto-Release Payments After X Days (For passive transactions)
export async function autoReleaseEscrowPayments() {
  try {
    const pendingEscrows = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.escrowCollectionId,
      [Query.equal("status", "pending")]
    );

    const now = Date.now(); // Get timestamp in milliseconds
    for (const escrow of pendingEscrows.documents) {
      const createdDate = new Date(escrow.$createdAt).getTime(); // Convert to timestamp
      const daysPassed = (now - createdDate) / (1000 * 60 * 60 * 24);

      if (daysPassed >= 7) {
        // Auto-generate sellerPayoutId if missing
        const sellerPayoutId = escrow.sellerPayoutId || ID.unique();
        await releasePayment(escrow.$id, sellerPayoutId);
      }
    }

    return true;
  } catch (error) {
    console.error("Error in autoReleaseEscrowPayments:", error);
    throw new Error("Failed to auto-release payments");
  }
}

// ✅ Dispute Transaction (Used when buyer or seller raises an issue)
export async function disputeTransaction(escrowId: string) {
  try {
    const escrow = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.escrowCollectionId,
      escrowId,
      {
        status: "disputed",
      }
    );

    return escrow;
  } catch (error) {
    console.error("Error in disputeTransaction:", error);
    throw new Error("Failed to mark transaction as disputed");
  }
}

/**
 * Find deal for specific buyer + seller (+ optional post) combination
 * When postId is provided: A buyer-seller pair can have multiple deals (for different posts)
 * Unique deal identifier: buyerId + sellerId + postId
 *
 * @param buyerId - The buyer's user ID
 * @param sellerId - The seller's user ID
 * @param postId - Optional post ID to filter by specific post
 * @param includeCompleted - If false (default), excludes completed deals. If true, includes all deals.
 */
export const getDealByBuyerAndSeller = async (
  buyerId: string,
  sellerId: string,
  postId?: string,
  includeCompleted: boolean = false
) => {
  try {
    const queries = [
      Query.equal("buyerId", buyerId),
      Query.equal("sellerId", sellerId),
      Query.orderDesc("$createdAt"),
    ];

    // Exclude completed deals unless explicitly requested
    if (!includeCompleted) {
      queries.push(Query.notEqual("status", "completed"));
    }

    // If postId is provided, filter by it for post-specific deals
    if (postId) {
      queries.push(Query.equal("postId", postId));
    }

    const res = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.dealStage,
      queries
    );
    return res.documents[0] || null;
  } catch (err) {
    console.error("Error fetching deal:", err);
    return null;
  }
};

export const getDealById = async (dealId: string): Promise<DealDocument | null> => {
  try {
    return (await databases.getDocument(
      appwriteConfig.databaseId,
      appwriteConfig.dealStage,
      dealId
    )) as unknown as DealDocument;
  } catch (error) {
    console.error("Error fetching deal by ID:", error);
    return null;
  }
};

export const createDealStatus = async ({
  buyerId,
  sellerId,
  postId,
  wasInitiated = true
}: {
  buyerId: string;
  sellerId: string;
  postId?: string;
  wasInitiated?: boolean;
}): Promise<DealDocument | null> => {
  try {
    const res = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.dealStage,
      ID.unique(),
      {
        buyerId,
        sellerId,
        postId: postId || null,
        amount: 0,
        requestedAmount: 0,
        status: "negotiation",
        wasInitiated,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    );
    return res;
  } catch (err) {
    console.error("Error creating deal:", err);
    return null;
  }
};

/**
 * Get all deals where user is either buyer or seller
 * Returns deals sorted by most recently updated
 */
export async function getUserDeals(userId: string): Promise<DealDocument[]> {
  try {
    // Get deals where user is buyer
    const buyerDeals = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.dealStage,
      [
        Query.equal("buyerId", userId),
        Query.orderDesc("updatedAt"),
        Query.limit(100)
      ]
    );

    // Get deals where user is seller
    const sellerDeals = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.dealStage,
      [
        Query.equal("sellerId", userId),
        Query.orderDesc("updatedAt"),
        Query.limit(100)
      ]
    );

    // Combine and sort by updatedAt
    const allDeals = [...buyerDeals.documents, ...sellerDeals.documents] as unknown as DealDocument[];
    allDeals.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

    // Remove duplicates (shouldn't happen but just in case)
    const uniqueDeals = allDeals.filter((deal, index, self) =>
      index === self.findIndex(d => d.$id === deal.$id)
    );

    return uniqueDeals;
  } catch (err) {
    console.error("Get user deals error:", err);
    return [];
  }
}

export async function getMonitoredDeals(limit = 25, offset = 0): Promise<{
  documents: DealDocument[];
  total: number;
  offset: number;
}> {
  const response = await backendRequest<{
    documents?: Record<string, any>[];
    total?: number;
    offset?: number;
  }>(`/api/v1/deals/monitoring?limit=${limit}&offset=${offset}`);
  return {
    documents: (response.documents ?? []).map(withAppwriteId) as DealDocument[],
    total: Number(response.total ?? 0),
    offset: Number(response.offset ?? offset),
  };
}

export async function getUserDealPage(userId: string, limit = 25, offset = 0): Promise<{
  documents: DealDocument[];
  total: number;
  offset: number;
}> {
  const params = new URLSearchParams({
    userId,
    limit: String(limit),
    offset: String(offset),
  });
  const response = await backendRequest<{
    documents?: Record<string, any>[];
    total?: number;
    offset?: number;
  }>(`/api/v1/deals?${params.toString()}`);
  return {
    documents: (response.documents ?? []).map(withAppwriteId) as DealDocument[],
    total: Number(response.total ?? 0),
    offset: Number(response.offset ?? offset),
  };
}

export const liveStreamCreateDealStatus = async ({
  buyerId,
  sellerId,
  amount,
  cashfreeOrderId,
  cashfreePaymentId,
  paymentVerifiedAt,
  wasInitiated = true
}: {
  buyerId: string;
  sellerId: string;
  amount: string;
  cashfreeOrderId: string;
  wasInitiated?: boolean;
  cashfreePaymentId: string;
  paymentVerifiedAt: string;
}): Promise<DealDocument | null> => {
  try {
    const res = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.dealStage,
      ID.unique(),
      {
        buyerId,
        sellerId,
        amount: amount,
        requestedAmount: amount,
        status: "paid",
        wasInitiated: wasInitiated,
        cashfreeOrderId,
        cashfreePaymentId,
        paymentVerifiedAt,
        escrowStatus: "funds_held", // Initial escrow status
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    );
    return res;
  } catch (err) {
    console.error("Error creating deal:", err);
    return null;
  }
};

export const updateDealStatus = async (
  dealId: string,
  updates: Partial<Omit<DealDocument, '$id' | 'createdAt' | 'buyerId' | 'sellerId'>>
): Promise<DealDocument | null> => {
  try {
    const res = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.dealStage,
      dealId,
      {
        ...updates,
        updatedAt: new Date().toISOString() // Always update timestamp
      }
    );
    return res;
  } catch (err) {
    console.error("Error updating deal:", err);
    return null;
  }
};

export const raiseDealDispute = async (
  dealId: string,
  reason: string
): Promise<DealDocument> => {
  const response = await backendRequest<any>(
    `/api/v1/deals/${encodeURIComponent(dealId)}/raise-dispute`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    }
  );
  return withAppwriteId({
    ...response,
    createdAt: response.createdAt ?? response.createdAtUtc,
    updatedAt: response.updatedAt ?? response.updatedAtUtc,
    deliveredAt: response.deliveredAt ?? response.deliveredAtUtc,
    disputeRaisedAt: response.disputeRaisedAt ?? response.disputeRaisedAtUtc,
  }) as DealDocument;
};

export const resolveDealDispute = async (
  dealId: string
): Promise<DealDocument> => {
  const response = await backendRequest<any>(
    `/api/v1/deals/${encodeURIComponent(dealId)}/resolve-dispute`,
    { method: "POST", body: JSON.stringify({}) }
  );
  return withAppwriteId({
    ...response,
    createdAt: response.createdAt ?? response.createdAtUtc,
    updatedAt: response.updatedAt ?? response.updatedAtUtc,
    deliveredAt: response.deliveredAt ?? response.deliveredAtUtc,
    disputeRaisedAt: response.disputeRaisedAt ?? response.disputeRaisedAtUtc,
  }) as DealDocument;
};

const mapDealTransition = (response: any): DealDocument => withAppwriteId({
  ...response,
  createdAt: response.createdAt ?? response.createdAtUtc,
  updatedAt: response.updatedAt ?? response.updatedAtUtc,
  deliveredAt: response.deliveredAt ?? response.deliveredAtUtc,
  disputeRaisedAt: response.disputeRaisedAt ?? response.disputeRaisedAtUtc,
  cancellationRequestedAt: response.cancellationRequestedAt ?? response.cancellationRequestedAtUtc,
  cancellationResponseDueAt: response.cancellationResponseDueAt ?? response.cancellationResponseDueAtUtc,
  cancellationRespondedAt: response.cancellationRespondedAt ?? response.cancellationRespondedAtUtc,
}) as DealDocument;

export const requestDealCancellation = async (dealId: string, reason: string): Promise<DealDocument> => {
  const response = await backendRequest<any>(`/api/v1/deals/${encodeURIComponent(dealId)}/request-cancellation`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
  return mapDealTransition(response);
};

export const declineDealCancellation = async (dealId: string): Promise<DealDocument> => {
  const response = await backendRequest<any>(`/api/v1/deals/${encodeURIComponent(dealId)}/decline-cancellation`, {
    method: "POST",
  });
  return mapDealTransition(response);
};

export const acceptDealCancellation = async (dealId: string): Promise<DealDocument> => {
  const response = await backendRequest<any>(`/api/v1/deals/${encodeURIComponent(dealId)}/accept-cancellation`, {
    method: "POST",
  });
  return mapDealTransition(response.deal ?? response);
};

export const acceptDisputeRefund = async (dealId: string): Promise<DealDocument> => {
  const response = await backendRequest<any>(`/api/v1/deals/${encodeURIComponent(dealId)}/accept-dispute-refund`, {
    method: "POST",
  });
  return mapDealTransition(response.deal ?? response);
};

export const startDealWork = async (dealId: string): Promise<DealDocument> => {
  const response = await backendRequest<any>(`/api/v1/deals/${encodeURIComponent(dealId)}/start-work`, { method: "POST" });
  return mapDealTransition(response);
};

export const markDealShipped = async (dealId: string): Promise<DealDocument> => {
  const response = await backendRequest<any>(`/api/v1/deals/${encodeURIComponent(dealId)}/ship`, { method: "POST" });
  return mapDealTransition(response);
};

export const markDealDelivered = async (dealId: string): Promise<DealDocument> => {
  const response = await backendRequest<any>(`/api/v1/deals/${encodeURIComponent(dealId)}/mark-delivered`, { method: "POST" });
  return mapDealTransition(response);
};

export const confirmDealDelivery = async (dealId: string): Promise<DealDocument> => {
  const response = await backendRequest<any>(`/api/v1/deals/${encodeURIComponent(dealId)}/confirm-delivery`, { method: "POST" });
  return mapDealTransition(response);
};

const mapDealAdjustment = (value: any): DealAdjustmentDocument => withAppwriteId(value) as DealAdjustmentDocument;

export const getDealAdjustments = async (dealId: string): Promise<DealAdjustmentDocument[]> => {
  const response = await backendRequest<any>(`/api/v1/deals/${encodeURIComponent(dealId)}/adjustments`);
  return (response.documents ?? []).map(mapDealAdjustment);
};

export const requestDealAdjustment = async (dealId: string, description: string): Promise<DealAdjustmentDocument> =>
  mapDealAdjustment(await backendRequest<any>(`/api/v1/deals/${encodeURIComponent(dealId)}/adjustments`, {
    method: "POST",
    body: JSON.stringify({ description }),
  }));

export const quoteDealAdjustment = async (dealId: string, adjustmentId: string, amount: number): Promise<DealAdjustmentDocument> =>
  mapDealAdjustment(await backendRequest<any>(`/api/v1/deals/${encodeURIComponent(dealId)}/adjustments/${encodeURIComponent(adjustmentId)}/quote`, {
    method: "POST",
    body: JSON.stringify({ amount }),
  }));

export const declineDealAdjustment = async (dealId: string, adjustmentId: string): Promise<DealAdjustmentDocument> =>
  mapDealAdjustment(await backendRequest<any>(`/api/v1/deals/${encodeURIComponent(dealId)}/adjustments/${encodeURIComponent(adjustmentId)}/decline`, {
    method: "POST",
  }));


export const getMyNotifications = async (userId: string) => {
  try {
    const res = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.notificationsCollectionId,
      [Query.equal("recipient_id", userId), Query.orderDesc("created_at")] // sorting by your created_at
    );
    return res.documents || [];
  } catch (err) {
    console.error("Error fetching notifications:", err);
    return [];
  }
};

// Mark a notification as read
export const markNotificationAsRead = async (notificationId: string) => {
  try {
    await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.notificationsCollectionId,
      notificationId,
      { is_read: true } // <-- use is_read with underscore
    );
  } catch (err) {
    console.error("Error marking notification as read:", err);
  }
};

export async function getKYCStatus(userId: string) {
  try {
    const kycData = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.kycRecordsCollectionId, // Make sure to add this to your appwriteConfig
      [Query.equal("userId", userId)]
    );

    if (!kycData || kycData.documents.length === 0) {
      return {
        panVerified: false,
        aadhaarVerified: false,
        bankVerified: false
      };
    }

    const doc = kycData.documents[0];
    const newStatus={
      panVerified: doc.panVerified || false,
      aadhaarVerified: doc.aadhaarVerified || false,
      bankVerified: doc.bankVerified || false,
      panVerifiedAt: doc.panVerifiedAt || null,
      aadhaarVerifiedAt: doc.aadhaarVerifiedAt || null,
      bankVerifiedAt: doc.bankVerifiedAt || null
    };
    return newStatus;
  } catch (error) {
    console.error('Error fetching KYC status:', error);
    return {
      panVerified: false,
      aadhaarVerified: false,
      bankVerified: false
    };
  }
}

export async function followUser({ 
  followerId, 
  followingId 
}: { 
  followerId: string; 
  followingId: string; 
}) {
  try {
    const follow = await backendRequest<any>("/api/v1/follows", {
      method: "POST",
      body: JSON.stringify({
        followerId,
        followingId,
      }),
    });

    const normalizedFollow = normalizeFollowRecord(follow, followerId, followingId);

    return normalizedFollow;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (message.includes("Already following this user")) {
      const existingFollow = await checkFollowStatus(followerId, followingId);
      if (existingFollow) {
        return existingFollow;
      }
    }

    console.error("Error following user:", error);
    throw error;
  }
}

// Unfollow a user
export async function unfollowUser(followId: string) {
  try {
    if (!followId) {
      throw new Error("Follow ID is required");
    }

    return await backendRequest<void>(`/api/v1/follows/${followId}`, {
      method: "DELETE",
    });
  } catch (error) {
    console.error("Error unfollowing user:", error);
    throw error;
  }
}

// Check if current user follows another user
export async function checkFollowStatus(followerId: string, followingId: string) {
  try {
    if (!followerId || !followingId) {
      return null;
    }

    // Don't allow users to follow themselves
    if (followerId === followingId) {
      return null;
    }

    const response = await backendRequest<any>(`/api/v1/follows/status?followerId=${encodeURIComponent(followerId)}&followingId=${encodeURIComponent(followingId)}`);
    if (!response?.isFollowing) {
      return null;
    }

    return normalizeFollowRecord(
      {
        id: response.followId ?? response.followId ?? null,
        followerId,
        followingId,
      },
      followerId,
      followingId
    );
  } catch (error) {
    console.error("Error checking follow status:", error);
    return null;
  }
}

// Get followers count for a user
export async function getFollowersCount(userId: string): Promise<number> {
  try {
    if (!userId) {
      return 0;
    }

    return await backendRequest<number>(`/api/v1/users/${userId}/followers/count`);
  } catch (error) {
    console.error("Error getting followers count:", error);
    return 0;
  }
}

// Get following count for a user
export async function getFollowingCount(userId: string): Promise<number> {
  try {
    if (!userId) {
      return 0;
    }

    return await backendRequest<number>(`/api/v1/users/${userId}/following/count`);
  } catch (error) {
    console.error("Error getting following count:", error);
    return 0;
  }
}

// Get followers list with pagination
export async function getFollowers(
  userId: string, 
  page: number = 1, 
  pageSize: number = 20
) {
  try {
    if (!userId) {
      return { documents: [], total: 0, page, pageSize, totalPages: 0 };
    }

    return await backendRequest<any>(`/api/v1/users/${userId}/followers?page=${page}&pageSize=${pageSize}`);
  } catch (error) {
    console.error("Error getting followers:", error);
    return { documents: [], total: 0, page, pageSize, totalPages: 0 };
  }
}

// Get following list with pagination
export async function getFollowing(
  userId: string, 
  page: number = 1, 
  pageSize: number = 20
) {
  try {
    if (!userId) {
      return { documents: [], total: 0, page, pageSize, totalPages: 0 };
    }

    return await backendRequest<any>(`/api/v1/users/${userId}/following?page=${page}&pageSize=${pageSize}`);
  } catch (error) {
    console.error("Error getting following:", error);
    return { documents: [], total: 0, page, pageSize, totalPages: 0 };
  }
}

// Get followers list with user details (populated)
export async function getFollowersWithDetails(
  userId: string, 
  page: number = 1, 
  pageSize: number = 20
) {
  try {
    return await getFollowers(userId, page, pageSize);
  } catch (error) {
    console.error("Error getting followers with details:", error);
    return { documents: [], total: 0, page, pageSize, totalPages: 0 };
  }
}

// Get following list with user details (populated)
export async function getFollowingWithDetails(
  userId: string, 
  page: number = 1, 
  pageSize: number = 20
) {
  try {
    return await getFollowing(userId, page, pageSize);
  } catch (error) {
    console.error("Error getting following with details:", error);
    return { documents: [], total: 0, page, pageSize, totalPages: 0 };
  }
}

// Check mutual follow status (if two users follow each other)
export async function checkMutualFollowStatus(userId1: string, userId2: string) {
  try {
    if (!userId1 || !userId2 || userId1 === userId2) {
      return { user1FollowsUser2: false, user2FollowsUser1: false };
    }

    const [follow1, follow2] = await Promise.all([
      checkFollowStatus(userId1, userId2),
      checkFollowStatus(userId2, userId1)
    ]);

    return {
      user1FollowsUser2: !!follow1,
      user2FollowsUser1: !!follow2,
      areMutualFollowers: !!follow1 && !!follow2
    };
  } catch (error) {
    console.error("Error checking mutual follow status:", error);
    return { user1FollowsUser2: false, user2FollowsUser1: false, areMutualFollowers: false };
  }
}

// Bulk check follow status for multiple users
export async function bulkCheckFollowStatus(followerId: string, userIds: string[]) {
  try {
    if (!followerId || userIds.length === 0) {
      return {};
    }

    // Filter out the follower's own ID
    const filteredUserIds = userIds.filter(id => id !== followerId);
    
    const promises = filteredUserIds.map(userId => 
      checkFollowStatus(followerId, userId).catch(() => null)
    );
    
    const results = await Promise.all(promises);
    
    return filteredUserIds.reduce((acc, userId, index) => {
      acc[userId] = results[index];
      return acc;
    }, {} as Record<string, any>);
  } catch (error) {
    console.error("Error bulk checking follow status:", error);
    return {};
  }
}

// Get suggested users to follow (users not already followed)
export async function getSuggestedUsersToFollow(
  currentUserId: string, 
  _limit: number = 10
) {
  try {
    if (!currentUserId) {
      return [];
    }

    // First, get users that the current user is already following
    const followingResponse = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.followsCollectionId,
      [
        Query.equal('followerId', currentUserId),
        Query.limit(1000) // Get all followed users
      ]
    );

    const followedUserIds = followingResponse.documents.map(doc => doc.followingId);
    followedUserIds.push(currentUserId); // Exclude self

    return [];
  } catch (error) {
    console.error("Error getting suggested users:", error);
    return [];
  }
}

export const getMultipleUsersByIds = async (userIds: string[]) => {
  if (!userIds || userIds.length === 0) {
    return [];
  }

  const uniqueIds = [...new Set(userIds.filter(id => id && id.trim()))];
  
  if (uniqueIds.length === 0) {
    return [];
  }

  try {
    const users = await backendRequest<any[]>("/api/v1/users/by-ids", {
      method: "POST",
      body: JSON.stringify({ userIds: uniqueIds }),
    });
    return Array.isArray(users)
      ? users.map((user) =>
          withAppwriteId({
            ...user,
            name: user.name ?? user.displayName ?? user.fullName ?? "",
            imageUrl: user.imageUrl ?? user.avatarUrl ?? user.photoUrl ?? "",
          })
        )
      : [];
  } catch (error) {
    console.error("Error fetching multiple users:", error);
    return [];
  }
};

export function validatePostData(post: INewPost | IUpdatePost): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Common validations
  if (!post.caption || post.caption.trim().length === 0) {
    errors.push("Caption is required");
  }

  if (post.caption && post.caption.length > 2000) {
    errors.push("Caption must be less than 2000 characters");
  }

  if (post.tags && typeof post.tags === 'string' && post.tags.split(',').length > 10) {
    errors.push("Maximum 10 tags allowed");
  }

  // Validation for new posts
  if ('userId' in post) {
    if (!post.userId) {
      errors.push("User ID is required");
    }
    if (!post.individual) {
      errors.push("Individual type is required");
    }
  }

  // Validation for update posts
  if ('postId' in post) {
    if (!post.postId) {
      errors.push("Post ID is required for update");
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}
